// BeforeAfterSection.jsx
// Bloc "Avant AbracadaBati / Avec AbracadaBati" — Premium Design

import { motion } from 'framer-motion';

const C = {
  orange: '#F97316',
  orangeDark: '#EA580C',
  navyDark: '#1a2a4a',
  green: '#22c55e',
};

export default function BeforeAfterSection() {
  return (
    <section style={{ 
      background: 'linear-gradient(180deg, #FFFBF7 0%, #fff 100%)', 
      position: 'relative', 
      overflow: 'hidden',
      padding: 'clamp(60px, 8vw, 100px) 0'
    }}>
      {/* Subtle decorative elements */}
      <div style={{ position: 'absolute', top: '10%', left: '-5%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,197,94,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
      
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
        {/* Titre premium - une seule ligne percutante */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: 20 }}
        >
          <span style={{ 
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(249,115,22,0.08)', 
            border: '1px solid rgba(249,115,22,0.2)',
            padding: '8px 16px', 
            borderRadius: 999, 
            marginBottom: 16,
            fontSize: 13, 
            fontWeight: 600, 
            color: C.orange,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />
            La réalité d'un entrepreneur
          </span>
          <h2 style={{ 
            fontFamily: "'Plus Jakarta Sans', sans-serif", 
            fontWeight: 800, 
            fontSize: 'clamp(26px, 4vw, 42px)', 
            color: C.navyDark,
            lineHeight: 1.2,
            marginBottom: 16
          }}>
            Tu as créé ton entreprise pour être <span style={{ color: C.orange }}>libre.</span>
          </h2>
          <p style={{ fontSize: 'clamp(18px, 2.5vw, 22px)', color: '#64748b', fontWeight: 500 }}>
            Pas pour devenir esclave de celle-ci.
          </p>
        </motion.div>
        
        {/* Flèche animée */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: 32 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
          >
            <span style={{ fontSize: 13, color: C.orange, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Découvre la différence</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: C.orange }}>
              <path d="M12 4v16m0 0l-6-6m6 6l6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
        </motion.div>
        
        {/* Cards Avant / Après - Design Premium */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 20, alignItems: 'stretch' }}>
          
          {/* AVANT */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            style={{
              background: 'linear-gradient(135deg, #fff 0%, #fef2f2 100%)',
              borderRadius: 24,
              padding: 32,
              border: '1px solid rgba(239,68,68,0.15)',
              boxShadow: '0 8px 32px rgba(239,68,68,0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Badge */}
            <div style={{ 
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(239,68,68,0.1)', 
              padding: '8px 16px', 
              borderRadius: 999, 
              marginBottom: 24,
              border: '1px solid rgba(239,68,68,0.2)'
            }}>
              <span style={{ fontSize: 16 }}>😓</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avant AbracadaBati</span>
            </div>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Tu réponds aux mails à 22h, épuisé',
                'Tu oublies de relancer un devis',
                'Tu n\'existes pas en ligne',
                'Tu passes 3h sur une facture',
                'Tu dois rappeler ton équipe',
                'Tu découvres les impayés par hasard',
                'Tu gères tout seul. Tu t\'épuises.',
              ].map((item, i) => (
                <motion.li 
                  key={i} 
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.05 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, fontSize: 14, color: '#64748b' }}
                >
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#ef4444', fontSize: 12 }}>✕</span>
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
          
          {/* Flèche centrale */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
              style={{ 
                width: 48, height: 48, borderRadius: '50%', 
                background: `linear-gradient(135deg, ${C.orange} 0%, ${C.orangeDark} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(249,115,22,0.3)'
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#fff' }}>
                <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </motion.div>
          </div>
          
          {/* APRÈS */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            style={{
              background: 'linear-gradient(135deg, #fff 0%, #f0fdf4 100%)',
              borderRadius: 24,
              padding: 32,
              border: '1px solid rgba(34,197,94,0.15)',
              boxShadow: '0 8px 32px rgba(34,197,94,0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Badge */}
            <div style={{ 
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(34,197,94,0.1)', 
              padding: '8px 16px', 
              borderRadius: 999, 
              marginBottom: 24,
              border: '1px solid rgba(34,197,94,0.2)'
            }}>
              <span style={{ fontSize: 16 }}>🚀</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avec AbracadaBati</span>
            </div>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Tes mails sont traités automatiquement',
                'Chaque devis est relancé au bon moment',
                'Ton contenu est publié chaque jour',
                'Tes factures partent automatiquement',
                'Le client a l\'info en temps réel',
                'L\'impayé est géré avant que tu le saches',
                'Tu as 12 experts. Ils ne dorment jamais.',
              ].map((item, i) => (
                <motion.li 
                  key={i} 
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.05 }}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, fontSize: 14, color: '#64748b' }}
                >
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 700 }}>✓</span>
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
